/**
 * Group entries into the Year → Month → Date hierarchy the modal renders.
 *
 * Multiple entries on the same release date roll up under one date header,
 * with build numbers shown as inline pills. Months and years bucket the
 * dates for the left-rail timeline.
 */

import type { Entry } from "../api";

export interface DateGroup {
  dateKey: string; // YYYY-MM-DD
  dateLabel: string; // "April 12, 2026"
  buildNumbers: string[];
  entries: Entry[];
}

export interface MonthSection {
  key: string; // "2026-04"
  monthLabel: string; // "Apr"
  monthLong: string; // "April 2026"
  year: number;
  unreadCount: number;
  dateGroups: DateGroup[];
}

export interface YearSection {
  year: number;
  unreadCount: number;
  months: MonthSection[];
}

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function parseDate(release_date: string): Date {
  // The backend returns YYYY-MM-DD; treat it as local-midnight so the day
  // doesn't shift across timezones for entries that don't care about time.
  // tsconfig's noUncheckedIndexedAccess marks each split chunk as possibly
  // undefined, so we fall through to sane defaults if the string is short.
  const parts = release_date.split("-").map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d);
}

// Safe index into the month name tables. Out-of-range falls back to an
// empty string so we never crash on bad data.
function monthShort(idx: number): string {
  return MONTH_SHORT[idx] ?? "";
}
function monthLong(idx: number): string {
  return MONTH_LONG[idx] ?? "";
}

export function groupEntries(entries: Entry[], lastReadAt: Date | null): YearSection[] {
  const sorted = [...entries].sort((a, b) => {
    if (a.release_date !== b.release_date) return a.release_date > b.release_date ? -1 : 1;
    return a.sort_order - b.sort_order;
  });

  const yearMap = new Map<number, YearSection>();
  const monthMap = new Map<string, MonthSection>();
  const dateMap = new Map<string, DateGroup>();

  for (const entry of sorted) {
    const date = parseDate(entry.release_date);
    const year = date.getFullYear();
    const month = date.getMonth();
    const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;
    const dateKey = entry.release_date;
    const isUnread = lastReadAt ? new Date(entry.created_at) > lastReadAt : true;

    let yearSection = yearMap.get(year);
    if (!yearSection) {
      yearSection = { year, unreadCount: 0, months: [] };
      yearMap.set(year, yearSection);
    }

    let monthSection = monthMap.get(monthKey);
    if (!monthSection) {
      monthSection = {
        key: monthKey,
        monthLabel: monthShort(month),
        monthLong: `${monthLong(month)} ${year}`,
        year,
        unreadCount: 0,
        dateGroups: [],
      };
      monthMap.set(monthKey, monthSection);
      yearSection.months.push(monthSection);
    }

    let dateGroup = dateMap.get(dateKey);
    if (!dateGroup) {
      dateGroup = {
        dateKey,
        dateLabel: `${monthLong(month)} ${date.getDate()}, ${year}`,
        buildNumbers: [],
        entries: [],
      };
      dateMap.set(dateKey, dateGroup);
      monthSection.dateGroups.push(dateGroup);
    }

    dateGroup.entries.push(entry);
    if (entry.build_number && !dateGroup.buildNumbers.includes(entry.build_number)) {
      dateGroup.buildNumbers.push(entry.build_number);
    }

    if (isUnread) {
      monthSection.unreadCount += 1;
      yearSection.unreadCount += 1;
    }
  }

  return Array.from(yearMap.values()).sort((a, b) => b.year - a.year);
}
